import { createReducer } from 'redux-starter-kit';
import {
  editDamageArea, cancelEditDamageArea, fetchDamageTypes, fetchModelTypes,
  fetchAddDamageArea, fetchDamageAreas, fetchUpdateDamageArea, addPublishDamageArea,
  removePublishDamageArea, fetchDamageTypesForArea, fetchDamageAreasPageData,
} from './damageAreaUiActions';
import { selectRequest } from '../../../domain/requests/RequestReducer';
import { selectDamageTypeMap } from '../../../domain/damageTypes/damageTypeReducer';
import { selectModelTypeMap } from '../../../domain/modelTypes/modelTypeReducer';


/**
 * A selector that returns the state of the 'FETCH_DAMAGE_AREAS_PAGE_DATA' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const pageDataRequest = selectDamageAreasPageDataRequest(state);
 */
export const selectDamageAreasPageDataRequest = (state) => {
  const requestId = state.via.ui.damageAreas.requestDamageAreasId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for finding the current edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the damage area being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.damageAreas.editState;

/**
 * A selector for finding the current publishing area codes.
 * @param {object} state The redux state of the application.
 * @returns An array of damage area codes.
 * @example const loadingAreaCodes = selectLoadingDamageAreaCodes(state);
 */
export const selectLoadingDamageAreaCodes = state => state.via.ui.damageAreas.pendingPublishDamageAreas;

/**
 * A selector that returns the state of the 'FETCH_DAMAGE_AREAS' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const damageAreaRequest = selectRequestedDamageAreas(state);
 */
export const selectDamageAreasRequest = (state) => {
  const requestId = state.via.ui.damageAreas.requestDamageAreasId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector that returns the state of the 'FETCH_DAMAGE_TYPES' action.
 * @param {state} state The redux state.
 * @returns An object representing the request state.
 * @example const damageTypeRequest = selectDamageTypesRequest(state);
 */
export const selectDamageTypesRequest = (state) => {
  const requestId = state.via.ui.damageAreas.requestDamageTypesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector that returns the currently loaded damage types.
 * Null if not currently loaded, otherwise a list of the damage types.
 * @param {state} state The redux state.
 * @returns Null if not loaded, or a list of the damage types.
 * @example const damageTypes = selectDamageTypes(state);
 */
export const selectDamageTypes = (state) => {
  const damageTypeMap = selectDamageTypeMap(state);
  const damageTypes = Object.values(damageTypeMap);

  return damageTypes || [];
};

/**
 * A selector that returns the state of the 'FETCH_MODEL_TYPES' action.
 * @param {state} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const modelTypesRequest = selectModelTypesRequest(state);
 */
export const selectModelTypesRequest = (state) => {
  const requestId = state.via.ui.damageAreas.requestModelTypesId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the model types loaded by the Damage Area Configuration screen.
 * @param {state} state The redux state for the application.
 * @returns The model types that were loaded.
 * @example const modelTypes = selectModelTypes(state);
 */
export const selectModelTypes = (state) => {
  const modelTypeMap = selectModelTypeMap(state);
  const modelTypes = Object.values(modelTypeMap);

  return modelTypes || [];
};

/**
 * A selector for the current 'FETCH_CREATE_DAMAGE_AREA' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addDamageAreasRequest = selectAddDamageAreaRequest(state);
 */
export const selectSubmitDamageAreaRequest = (state) => {
  const requestId = state.via.ui.damageAreas.editState.requestSubmitDamageAreaId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};

/**
 * A selector for the current 'FETCH_CREATE_DAMAGE_AREA' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addDamageAreasRequest = selectAddDamageAreaRequest(state);
 */
export const selectFetchDamageTypesForAreaRequest = (state) => {
  const requestId = state.via.ui.damageAreas.editState.requestDamageTypesForAreaId;
  if (!requestId) return null;

  return selectRequest(state, requestId);
};


const intitialEditingState = {
  isEditing: false,
  damageAreaCode: null,
  selectedDamageTypes: [],
  requestAddDamageTypeId: null,
  requestSubmitDamageAreaId: null,
  requestDamageTypesForAreaId: null,
};

const damageAreasInitialState = {
  editState: intitialEditingState, // Is a damage area being edited/created.
  pendingPublishDamageAreas: [], // Damage areas being submitted for publishing.
  requestDamageAreasId: null, // The request id for loading damage areas.
  requestDamageTypesId: null, // The request id for loading damage types.
  requestModelTypesId: null, // The request id for loading model types.
};

/**
 * A reducer for handling the UI state changes for the Damage Area Configuration screen.
 */
const damageAreasReducer = createReducer(damageAreasInitialState, {
  // The user has started editing the damage area.
  [editDamageArea]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      isEditing: true,
      damageAreaCode: action.payload,
    },
  }),

  // The user has stopped editing the damage area.
  [cancelEditDamageArea]: state => ({
    ...state,
    editState: {
      ...intitialEditingState,
    },
  }),

  // A new requst has been made to add a damage area.
  [fetchAddDamageArea]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitDamageAreaId: action.payload,
    },
  }),

  // A new request has been made to update a damage area.
  [fetchUpdateDamageArea]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestSubmitDamageAreaId: action.payload,
    },
  }),

  [fetchDamageTypesForArea]: (state, action) => ({
    ...state,
    editState: {
      ...state.editState,
      requestDamageTypesForAreaId: action.payload,
    },
  }),

  // A damage area is being published. So add it to state.
  [addPublishDamageArea]: (state, action) => ({
    ...state,
    pendingPublishDamageAreas: [...state.pendingPublishDamageAreas, action.payload],
  }),

  // A damage area has finished publishing. Remove it from state.
  [removePublishDamageArea]: (state, action) => ({
    ...state,
    pendingPublishDamageAreas: state.pendingPublishDamageAreas.filter(code => `${code}` !== `${action.payload}`),
  }),

  // A new request has been made for the damage areas.
  [fetchDamageAreas]: (state, action) => ({
    ...state,
    requestDamageAreasId: action.payload,
  }),

  // A new request has been made for damage types.
  [fetchDamageTypes]: (state, action) => ({
    ...state,
    requestDamageTypesId: action.payload,
  }),

  // A new request has been made for the model types.
  [fetchModelTypes]: (state, action) => ({
    ...state,
    requestModelTypesId: action.payload,
  }),

  // A new request has been made for the loading page data.
  [fetchDamageAreasPageData]: (state, action) => ({
    ...state,
    requestDamageAreasId: action.payload,
  }),
});

export default damageAreasReducer;
